import { useMutation } from '@tanstack/react-query'
import { ECO_THUMBS_DOWN, type AssistantToolCallView, type AssistantTurnView } from '@fieldops/shared'
import { useEffect, useLayoutEffect, useRef, useState, type ReactNode } from 'react'
import { cn } from '../../lib/cn.js'
import { errorMessage, useTRPC } from '../../lib/trpc.js'
import { Icon } from '../Icon.js'
import { ActionCard } from '../job-card/assistant/ActionCard.js'
import { Markdown } from '../job-card/assistant/Markdown.js'
import { Display } from './displays.js'
import { EcoChip } from './EcoButtons.js'

/**
 * One answer from Eco, however many rounds it took.
 *
 * The model answers in rounds — look up the client, open them, check the
 * diary, then speak — and each round was drawn as a block of its own. Here
 * they are one: the working steps folded into a single line that opens to the
 * list, then the cards and pictures, then the words, held to a height when
 * they run long. Under a finished answer are the thumbs, Copy and the time; a
 * thumbs-down asks what was wrong and offers Try again or Send to the backlog,
 * and an answer that says Eco cannot do something offers to ask the team for
 * it. Decision 116, 2026-11-01.
 */

export type Entry = { kind: 'user'; turn: AssistantTurnView } | { kind: 'eco'; id: string; turns: AssistantTurnView[] }

/** The conversation as people read it: each message, and each answer as one. */
export function groupTurns(turns: readonly AssistantTurnView[]): Entry[] {
  const out: Entry[] = []
  for (const turn of turns) {
    const last = out.at(-1)
    if (turn.role === 'USER') out.push({ kind: 'user', turn })
    else if (last?.kind === 'eco') last.turns.push(turn)
    else out.push({ kind: 'eco', id: turn.id, turns: [turn] })
  }
  return out
}

type Piece =
  | { kind: 'text'; turn: AssistantTurnView }
  | { kind: 'steps'; calls: AssistantToolCallView[] }
  | { kind: 'call'; call: AssistantToolCallView }

/** A reply this long is held to a height with "Show the whole answer". */
const LONG_REPLY = 1100

/** Words that mean "I can't do that" — the moment to offer a feature request. */
const CANNOT =
  /\b(I can(?:no|['’])t|I(?:['’]m| am) (?:not able|unable)|I don['’]t have (?:a|any|the) (?:tool|way)|not something I can|isn['’]t something I can|there(?:['’]s| is) no way for me)\b/i

function piecesOf(turns: readonly AssistantTurnView[]): Piece[] {
  const pieces: Piece[] = []
  const step = (call: AssistantToolCallView) => {
    const last = pieces.at(-1)
    if (last?.kind === 'steps') last.calls.push(call)
    else pieces.push({ kind: 'steps', calls: [call] })
  }
  for (const turn of turns) {
    if (turn.content.trim()) pieces.push({ kind: 'text', turn })
    for (const call of turn.toolCalls) {
      // A reading step, and a proposal the model got wrong before there was
      // anything to show, are working steps; the rest stand on their own.
      const quiet = call.tier === 'read' || (call.status === 'FAILED' && !call.card)
      if (quiet) step(call)
      if (!quiet || call.display) pieces.push({ kind: 'call', call })
    }
  }
  return pieces
}

export interface EcoReplyProps {
  turns: AssistantTurnView[]
  /** Still arriving: no footer yet, and the words streaming in after it. */
  live: boolean
  streaming?: string
  busy: boolean
  /** What the person asked, for a retry or a report. */
  asked: string | null
  onApprove: (call: AssistantToolCallView, edits?: Record<string, unknown>) => void
  onReject: (call: AssistantToolCallView, reason?: string, resume?: boolean) => void
  onUndo: (call: AssistantToolCallView) => void
  undoing: (call: AssistantToolCallView) => boolean
  onPick: (prompt: string) => void
  onRetry: (why: string) => void
  onReport: (draft: { kind: 'bug' | 'feature'; summary: string; details: string }) => void
}

export function EcoReply(props: EcoReplyProps) {
  const { turns, live, streaming, busy } = props
  const pieces = piecesOf(turns)
  const texts = turns.filter((turn) => turn.content.trim())
  const answer = texts.at(-1) ?? null
  const words = texts.map((turn) => turn.content.trim()).join('\n\n')
  const offersReport = turns.some((turn) => turn.toolCalls.some((call) => call.card?.kind === 'report'))
  const cannot = !live && !offersReport && CANNOT.test(answer?.content ?? '')

  return (
    <article aria-label="Eco’s answer" className="flex flex-col gap-2.5">
      {pieces.map((piece, index) =>
        piece.kind === 'steps' ? (
          <Steps key={`s${index}`} calls={piece.calls} />
        ) : piece.kind === 'call' ? (
          <div key={piece.call.id} className="flex flex-col gap-2" data-waiting={piece.call.status === 'AWAITING_APPROVAL' ? '' : undefined}>
            {piece.call.tier !== 'read' ? (
              <ActionCard
                call={piece.call}
                busy={busy}
                undoing={props.undoing(piece.call)}
                onApprove={(edits) => props.onApprove(piece.call, edits)}
                onReject={(reason, resume) => props.onReject(piece.call, reason, resume)}
                onUndo={() => props.onUndo(piece.call)}
              />
            ) : null}
            {piece.call.display ? <Display display={piece.call.display} busy={busy} onPick={props.onPick} /> : null}
          </div>
        ) : (
          <Words key={piece.turn.id} text={piece.turn.content} />
        ),
      )}
      {streaming ? <Words text={streaming} /> : null}

      {cannot ? (
        <div className="flex flex-wrap items-center gap-2">
          <EcoChip
            onClick={() =>
              props.onReport({
                kind: 'feature',
                summary: (props.asked ?? 'Something Eco cannot do yet').slice(0, 200),
                details: `I asked Eco: “${props.asked ?? ''}”\nIt said: “${(answer?.content ?? '').slice(0, 600)}”`,
              })
            }
          >
            <Icon name="flag" className="size-3.5" />
            Ask the team to add this
          </EcoChip>
        </div>
      ) : null}

      {!live && answer ? <Footer answer={answer} words={words} {...props} /> : null}
    </article>
  )
}

/** The working steps as one line — "4 steps: looked up…" — that opens to the list. */
function Steps({ calls }: { calls: AssistantToolCallView[] }) {
  const [open, setOpen] = useState(false)
  if (calls.length === 1) return <StepLine call={calls[0]!} />
  const failed = calls.filter((call) => call.status === 'FAILED').length
  return (
    <div className="flex flex-col gap-1">
      <button
        type="button"
        aria-expanded={open}
        onClick={() => setOpen((was) => !was)}
        className="flex min-w-0 items-center gap-1.5 self-start text-left text-[12px] text-ink-500 hover:text-ink-900"
      >
        <Icon name={open ? 'chevronDown' : 'chevronRight'} className="size-3.5 shrink-0" />
        <span className="truncate">
          <span className="font-medium text-ink-600">{calls.length} steps</span>
          {failed ? ` (${failed} tried again)` : ''}: {calls.map((call) => call.label.toLowerCase()).join(', ')}
        </span>
      </button>
      {open ? (
        <div className="flex flex-col gap-1 pl-5">
          {calls.map((call) => (
            <StepLine key={call.id} call={call} />
          ))}
        </div>
      ) : null}
    </div>
  )
}

function StepLine({ call }: { call: AssistantToolCallView }) {
  const failed = call.status === 'FAILED'
  return (
    <p className={cn('flex items-start gap-1.5 text-[12px]', failed ? 'text-ink-400' : 'text-ink-500')}>
      <Icon name={failed ? 'alert' : 'check'} className="mt-px size-3.5 shrink-0" />
      <span>
        {call.label}
        {failed && call.error ? ` — ${call.error}` : null}
      </span>
    </p>
  )
}

/** The words, held to a height when they run long. */
function Words({ text }: { text: string }) {
  const long = text.length > LONG_REPLY
  const [open, setOpen] = useState(false)
  const box = useRef<HTMLDivElement>(null)
  const [overflows, setOverflows] = useState(long)
  useLayoutEffect(() => {
    const node = box.current
    if (node && long && !open) setOverflows(node.scrollHeight > node.clientHeight + 4)
  }, [long, open, text])

  return (
    <div className="rounded-lg bg-white px-3.5 py-2.5 ring-1 ring-ink-200 ring-inset">
      <div ref={box} className={cn('relative', long && !open && 'max-h-[19rem] overflow-hidden')}>
        <Markdown text={text} />
        {long && !open && overflows ? (
          <div aria-hidden="true" className="pointer-events-none absolute inset-x-0 bottom-0 h-16 bg-gradient-to-t from-white to-transparent" />
        ) : null}
      </div>
      {long && overflows ? (
        <button
          type="button"
          aria-expanded={open}
          onClick={() => setOpen((was) => !was)}
          className="mt-1.5 text-[12.5px] font-semibold text-brand-700 hover:underline"
        >
          {open ? 'Show less' : 'Show the whole answer'}
        </button>
      ) : null}
    </div>
  )
}

function Footer({
  answer,
  words,
  asked,
  busy,
  onRetry,
  onReport,
}: EcoReplyProps & { answer: AssistantTurnView; words: string }) {
  const trpc = useTRPC()
  const [rating, setRating] = useState<1 | -1 | null>(answer.rating)
  const [reason, setReason] = useState<string | null>(answer.ratingNote)
  const [asking, setAsking] = useState(false)
  const [copied, setCopied] = useState(false)
  const rate = useMutation(trpc.assistant.rate.mutationOptions())
  const box = useRef<HTMLDivElement>(null)

  // The saved thumb wins once it arrives: the panel may have drawn this answer
  // from the stream before the rating was read back.
  useEffect(() => {
    setRating(answer.rating)
    setReason(answer.ratingNote)
  }, [answer.rating, answer.ratingNote])

  // The question opens below the answer; bring it into view.
  useEffect(() => {
    if (asking) box.current?.scrollIntoView({ block: 'nearest', behavior: 'smooth' })
  }, [asking])

  const thumb = (next: 1 | -1) => {
    const value = rating === next ? null : next
    setRating(value)
    setAsking(value === -1)
    if (value !== -1) setReason(null)
    rate.mutate({ turnId: answer.id, rating: value, note: null })
  }

  const why = (choice: string) => {
    setReason(choice)
    rate.mutate({ turnId: answer.id, rating: -1, note: choice })
  }

  const time = new Intl.DateTimeFormat('en-AU', { hour: 'numeric', minute: '2-digit' }).format(new Date(answer.occurredAt))

  return (
    <div className="flex flex-col gap-2">
      <div className="flex items-center gap-0.5 text-ink-400">
        <FooterButton label="Good answer" pressed={rating === 1} onClick={() => thumb(1)}>
          <Icon name="thumbUp" className="size-4" />
        </FooterButton>
        <FooterButton label="Not a good answer" pressed={rating === -1} onClick={() => thumb(-1)}>
          <Icon name="thumbDown" className="size-4" />
        </FooterButton>
        <FooterButton
          label={copied ? 'Copied' : 'Copy the answer'}
          onClick={() => {
            void navigator.clipboard?.writeText(words).catch(() => undefined)
            setCopied(true)
            window.setTimeout(() => setCopied(false), 1500)
          }}
        >
          <Icon name={copied ? 'check' : 'copy'} className="size-4" />
        </FooterButton>
        <span className="ml-1.5 text-[11px] tabular-nums">{time}</span>
        {rating === 1 ? <span className="ml-2 text-[11.5px] text-brand-700">Thanks — noted.</span> : null}
        {rate.error ? <span className="ml-2 text-[11.5px] text-red-700">{errorMessage(rate.error)}</span> : null}
      </div>

      {rating === -1 && asking ? (
        <div ref={box} className="flex flex-col gap-2 rounded-lg border border-ink-200 bg-white px-3 py-2.5" role="group" aria-label="What was wrong?">
          <p className="text-[12.5px] font-semibold text-ink-900">What was wrong?</p>
          <div className="flex flex-wrap gap-1.5">
            {ECO_THUMBS_DOWN.map((choice) => (
              <EcoChip key={choice} aria-pressed={reason === choice} onClick={() => why(choice)}>
                {choice}
              </EcoChip>
            ))}
          </div>
          <div className="flex flex-wrap items-center gap-3 pt-0.5 text-[12.5px] font-semibold">
            <button
              type="button"
              disabled={busy}
              onClick={() => {
                setAsking(false)
                onRetry(reason ?? 'That wasn’t right')
              }}
              className="text-brand-700 hover:underline disabled:opacity-50"
            >
              Try again
            </button>
            <button
              type="button"
              onClick={() => {
                setAsking(false)
                onReport({
                  kind: reason === 'Didn’t do what I asked' ? 'feature' : 'bug',
                  summary: `${reason ?? 'A wrong answer'}: ${asked ?? 'Eco’s answer'}`.slice(0, 200),
                  details: `I asked Eco: “${asked ?? ''}”\nIt answered: “${words.slice(0, 900)}”\nWhat was wrong: ${reason ?? '—'}`,
                })
              }}
              className="text-brand-700 hover:underline"
            >
              Send to the backlog
            </button>
            <button type="button" onClick={() => setAsking(false)} className="font-medium text-ink-500 hover:text-ink-900">
              Done
            </button>
          </div>
        </div>
      ) : null}
    </div>
  )
}

function FooterButton({ label, pressed, onClick, children }: { label: string; pressed?: boolean; onClick: () => void; children: ReactNode }) {
  return (
    <button
      type="button"
      aria-label={label}
      title={label}
      aria-pressed={pressed}
      onClick={onClick}
      className={cn('grid size-7 place-items-center rounded-md hover:bg-ink-100 hover:text-ink-900', pressed && 'bg-ink-900 text-white hover:bg-ink-900 hover:text-white')}
    >
      {children}
    </button>
  )
}
